/**
 * errorHandlerProvider
 *
 * @author Dec Norton <decnorton@gmail.com>
 */

import { getValidationMessages } from '@shared/services/error-handler/helpers';
import isObject from 'lodash-es/isObject';
import { ApiError, RequestAbortedError } from '../../app/fetchers/api-error';


export interface AppErrorHandler {
    handleErrorResponse: (response, form?: any) => (Promise<never>);
    toast: (message) => any;
    handle: (response, form?: any) => (Promise<never>);
    isValid: (form) => (boolean | any);
    isInvalid: (form) => boolean;
    message: (error) => (string);
    getValidationMessages: (response) => (undefined | any[]);
    isApiError: (test) => boolean;
}

export function errorHandlerProvider() {
    this.$get = $get;

    $get.$inject = ['$q', '$toast', '$log', '$util'];

    function $get($q, $toast, $log, $util) {
        return {
            handleErrorResponse,
            toast,
            handle: handleErrorResponse, // Alias
            isValid: isFormValid,
            isInvalid: isFormInvalid,

            message: getErrorMessage,
            getValidationMessages: getValidationMessages,

            isApiError: ApiError.instanceOf,
        } as AppErrorHandler;

        function parseErrorResponse(response) {
            if (response.data && response.data.error) {
                return new ApiError(response.data.error);
            }

            return response;
        }

        function handleErrorResponse(response, form = null) {
            if (RequestAbortedError.instanceOf(response)) {
                // Ignore aborted network requests
                return $q.reject(response);
            } else {
                response = parseErrorResponse(response);
            }

            let errorMessage = getErrorMessage(response);

            if (!window.navigator.onLine && response.status === -1) {
                errorMessage = 'Internet connection is offline';
            }

            $log.error(errorMessage, response);

            if (response && response.type) {
                switch (response.type) {
                    case 'missing_gcm_token':
                        return toast(`App installation isn't registered for push notifications`);
                }
            }

            if (response && response.code) {
                switch (response.code) {
                    case 422:
                        return handleValidationResponse(form, response);
                }
            }

            toast(errorMessage);

            return $q.reject(response);
        }

        function isFormValid(form) {
            if (!form || form.$valid) {
                return true;
            }

            const firstInvalidControl = form.$$controls.find(_ => _.$invalid);

            if (firstInvalidControl) {
                const element = firstInvalidControl.$$element;

                if (element) {
                    element.focus();
                }
            }

            return form.$valid;
        }

        function isFormInvalid(form) {
            return !isFormValid(form);
        }

        function handleValidationResponse(form, response) {
            // Check we can see which fields failed
            if (!angular.isObject(response.fields)) {
                toast(response.message || `Some fields are invalid`);
                return $q.reject(response);
            }

            const messages = [];

            // Reset all custom errors
            if (form) {
                form.$$controls.forEach(control => {
                    $util.nextTick(() => {
                        control.$setValidity('custom', true);
                        control.customError = null;
                    }, true);
                });
            }

            for (const field of Object.keys(response.fields)) {
                const fieldErrors = response.fields[field];

                if (!fieldErrors.length) {
                    continue;
                }

                const message = fieldErrors[0];

                messages.push(message);

                if (form && form.hasOwnProperty(field)) {
                    $util.nextTick(() => {
                        form[field].$setValidity('custom', false);
                        form[field].customError = message;
                    }, true);
                }
            }

            if (!messages.length) {
                toast(response.message || `Some fields are invalid`);
            } else {
                // Only toast the first message as we can't use multi-line or multiple toasts.
                toast(messages[0]);
            }

            // Pass back the original response
            return $q.reject(response);
        }

        function toast(message) {
            return $toast.errorToast(message);
        }
    }
}

export function getErrorMessage(error): string {
    if (ApiError.instanceOf(error)) {
        if (error.code === 422) {
            const validationErrorMessages = error.validationErrorMessages;

            if (validationErrorMessages.length > 0) {
                return error.validationErrorMessages.join('\n');
            }
        }
    }

    return ApiError.instanceOf(error) || (isObject(error) && error.message)
        ? error.message
        : 'A problem occurred';
}
